import { useCallback, useEffect, useRef, useMemo } from 'react';

export const useDebounceApiCall = (
    apiCallback,
    dependencies,
    options = {}
) => {
    const {
        textSearchFields = [],
        textSearchDelay = 700,
        immediateDelay = 0,
        condition = true
    } = options;

    const timeoutRef = useRef(null);

    // Check if any text search fields have values
    const hasTextSearch = useMemo(() => {
        return textSearchFields.some(field => field && field.length > 0);
    }, [textSearchFields]);

    const delay = hasTextSearch ? textSearchDelay : immediateDelay;

    const debouncedCallback = useCallback(() => {
        if (condition) {
            apiCallback();
        }
    }, [apiCallback, condition]);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(debouncedCallback, delay);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [...dependencies, debouncedCallback, delay]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    return { cancel };
};
